import React, {Component} from 'react';
import {FormattedMessage, injectIntl} from 'react-intl';
import type {WrappedComponentProps} from 'react-intl';
import {withRouter} from 'react-router-dom';
import type {RouteComponentProps} from 'react-router-dom';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../constants';
import Util from '../../forecast-app/shared/util/util';
import InlineLoader from '../../forecast-app/shared/components/inline-loader/inline_loader';
import GenericModal from './generic_modal';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import MigrateXeroClientsMutation from '../../mutations/MigrateXeroClientsMutation';
import {Checkbox} from '@/web-components';
import {hideLoader, showLoader} from '../global_loader';
import {hasPermission} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import DirectApi from '../../directApi';
import XeroMatchReviewStep from './xero_match_review/xero_match_review_step';
import {ignoredContactIds, importCount, toResolutions} from './xero_match_review/resolution_state';
import type {GroupChoice, MatchGroup, Resolution, Selection} from './xero_match_review/resolution_state';
import {finishClientImport, loadImportContacts, prepareImportBillTos} from './importClientsBillTo';
import type {BillFromRef, Contact} from '../../forecast-app/admin-tab/finance-page/InvoiceIntegrationsUtils';
import type {MigrateXeroClientsMutation$data} from '../../mutations/__generated__/MigrateXeroClientsMutation.graphql';

/** A contact from xero/unimportedContacts. */
type XeroContact = {id: string; name: string; org: string};

/** One page of xero/unimportedContacts, which pages through every connected tenant in turn. */
type UnimportedContactsPage = {
	unimportedContacts: XeroContact[];
	moreCustomersToFetch: boolean;
	page: number;
	currentTenantIdIndex: number;
};

/** The response of xero/importMatchPreview. */
type MatchPreview = {groups?: MatchGroup[] | null; autoLinkCount?: number | null; createCount?: number | null};

type Props = WrappedComponentProps &
	RouteComponentProps & {
		closeModal: () => void;
		billFroms: readonly BillFromRef[] | null;
	};

type State = {
	xeroContacts: XeroContact[];
	fetchingContacts: boolean;
	selectedContacts: string[];
	step: 'select' | 'review';
	matchGroups: MatchGroup[];
	autoLinkCount: number;
	createCount: number;
	selection: Selection;
	previewing: boolean;
	selectAll?: boolean;
	importing?: boolean;
};

class XeroSyncClientsModal extends Component<Props, State> {
	constructor(props: Props) {
		super(props);

		this.state = {
			xeroContacts: [],
			fetchingContacts: true,
			selectedContacts: [],
			// T4-135. 'select' is the step that has always existed; 'review' only ever appears when
			// the preview found a match that needs a human decision.
			step: 'select',
			matchGroups: [],
			autoLinkCount: 0,
			createCount: 0,
			selection: {},
			previewing: false,
		};

		// Started now so it overlaps with the user picking contacts; resolves [] with the flag off.
		this.importContacts = loadImportContacts(props.billFroms, {newXeroEnabled: true});

		this.getContacts();
	}

	/** The contacts behind the list, for the Bill To lookup; see loadImportContacts. */
	importContacts: Promise<Contact[]>;

	getContactsHelper(page: number, currentTenantIdIndex: number): Promise<void> {
		// Recursive function
		return DirectApi.Fetch_Raw(`xero/unimportedContacts?page=${page}&currentTenantIdIndex=${currentTenantIdIndex}`).then(
			(result: Response) => {
				if (result.status === 403) {
					this.props.closeModal();
					showModal({
						type: MODAL_TYPE.XERO_INACCESSIBLE,
						isAdminUser: hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS),
					});
					return;
				}

				return result.json().then((result: UnimportedContactsPage) => {
					if (result.moreCustomersToFetch) {
						this.setState({
							xeroContacts: this.state.xeroContacts.concat(result.unimportedContacts),
						});
						this.getContactsHelper(result.page, result.currentTenantIdIndex);
					} else {
						this.setState({
							xeroContacts: this.state.xeroContacts.concat(result.unimportedContacts).sort((a, b) => {
								if (a.name.toUpperCase().trim() > b.name.toUpperCase().trim()) {
									return 1;
								} else if (a.name.toUpperCase().trim() < b.name.toUpperCase().trim()) {
									return -1;
								}
								return 0;
							}),
							fetchingContacts: false,
						});
						return;
					}
				});
			}
		);
	}

	getContacts() {
		// We initially pass 0s as the Xero logic undestands it as the start
		this.getContactsHelper(0, 0).then(() => {
			return;
		});
	}

	toggleSelectAll() {
		if (this.state.selectAll) {
			this.setState({
				selectedContacts: [],
				selectAll: false,
			});
		} else {
			this.setState({
				selectedContacts: this.state.xeroContacts.map(xeroContact => xeroContact.id),
				selectAll: true,
			});
		}
	}

	toggleContact(xeroContactTd: string) {
		if (this.state.selectedContacts.includes(xeroContactTd)) {
			const selectedContacts = this.state.selectedContacts.filter(id => id !== xeroContactTd);
			this.setState({
				selectedContacts,
				selectAll: false,
			});
		} else {
			const selectedContacts = this.state.selectedContacts;
			selectedContacts.push(xeroContactTd);
			this.setState({
				selectedContacts,
			});
		}
	}

	/**
	 * T4-135. Ask which of the selected customers need a decision before importing anything.
	 *
	 * Read-only: nothing is created, linked or queued by this call, which is what lets the user
	 * close the modal from the review step having changed nothing.
	 *
	 * A failure here is deliberately not fatal. The import behind it is T4-106's, which handles
	 * every ambiguous case safely by ignoring it - so a failed preview degrades to the behaviour
	 * that shipped before this story rather than blocking an import that would have worked.
	 */
	reviewMatches() {
		this.setState({previewing: true});
		showLoader();

		const init: RequestInit = {
			method: 'POST',
			headers: {'Content-Type': 'application/json'},
			credentials: 'include',
			body: JSON.stringify({xeroContactIds: this.state.selectedContacts}),
		};

		return DirectApi.Fetch_Raw_With_Init('xero/importMatchPreview', init)
			.then((result: Response) => {
				if (result.status === 403) {
					hideLoader();
					this.setState({previewing: false});
					this.props.closeModal();
					showModal({
						type: MODAL_TYPE.XERO_INACCESSIBLE,
						isAdminUser: hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS),
					});
					return;
				}

				return result.json().then((preview: MatchPreview | null) => {
					const groups = (preview && preview.groups) || [];

					hideLoader();
					this.setState({previewing: false});

					if (groups.length === 0) {
						// Explicitly no resolutions, rather than syncContacts() reading state. A
						// previous trip through the review step leaves matchGroups and selection
						// set, and BACK preserves them by design - so if the user then changes
						// their selection and the new preview finds nothing to resolve, reading
						// state here would send pairings the user did not confirm this time.
						this.importSelected([], []);
						return;
					}

					this.setState({
						step: 'review',
						matchGroups: groups,
						autoLinkCount: preview?.autoLinkCount || 0,
						createCount: preview?.createCount || 0,
						selection: {},
					});
				});
			})
			.catch(() => {
				hideLoader();
				this.setState({previewing: false});
				// The import behind this is T4-106's, which handles every ambiguous case safely by
				// ignoring it - so a failed preview degrades to the behaviour that shipped before
				// this story rather than blocking an import that would have worked.
				this.importSelected([], []);
			});
	}

	selectResolution(groupKey: string, choice: GroupChoice) {
		this.setState(state => ({selection: {...state.selection, [groupKey]: choice}}));
	}

	backToSelection() {
		// The ticks survive, which is why the review is a step of this component rather than a
		// modal of its own - going back does not re-walk the paged unimported-contacts fetch.
		//
		// The resolutions do not: coming back into the review runs a fresh preview, and that
		// clears the selection deliberately. The groups it returns are rebuilt from data that may
		// have moved, so a pairing made against the previous set could be keyed to a group whose
		// shape has changed. Re-deciding costs a few clicks; carrying a stale answer forward
		// silently would not be recoverable by the user.
		this.setState({step: 'select'});
	}

	/**
	 * Import the selection with exactly these pairings.
	 *
	 * The resolutions and the groups they answer are parameters rather than something read from
	 * state, so no call site can accidentally carry a previous review round's answers.
	 */
	async importSelected(resolutions: Resolution[], reviewedGroups: MatchGroup[]) {
		this.setState({importing: true});
		showLoader();
		// Contacts the review leaves unpaired are ignored by the import, so they get no Bill To.
		const ignored = ignoredContactIds(reviewedGroups, resolutions);
		const {billTos, skippedCount} = await prepareImportBillTos(
			this.props.billFroms,
			'XERO',
			this.state.selectedContacts.filter(id => !ignored.has(id)),
			this.importContacts
		);
		Util.CommitMutation(
			MigrateXeroClientsMutation,
			{
				xeroClientIds: this.state.selectedContacts,
				// Only the confirmed pairings. A client left on "do not link", an unchosen customer
				// and an unresolved group are all absent, which the backend reads as "not linked".
				resolutions,
				...(billTos ? {billTos} : {}),
			},
			(response: MigrateXeroClientsMutation$data) => {
				this.setState({importing: false});
				this.props.closeModal();
				hideLoader();
				finishClientImport(
					skippedCount,
					response?.migrateXeroClients?.unlinkedBillToCount,
					this.props.intl.formatMessage
				);
			}
		);
	}

	render() {
		const {formatMessage} = this.props.intl;
		const reviewing = this.state.step === 'review';
		/*
		 * What pressing Import will actually bring in: the auto-matched, the ones becoming new
		 * clients, and every pairing confirmed so far. Per the AC added 2026-09-15 one confirmed
		 * match is enough to enable it, with other groups left unresolved and their contacts
		 * skipped - so it is not gated on every card being finished. Disabled only when the import
		 * would do nothing at all.
		 */
		const willImport = importCount(
			this.state.matchGroups,
			this.state.selection,
			this.state.autoLinkCount,
			this.state.createCount
		);

		const selectContent = (
			<div className="integration-sync-clients-modal-content">
				<div className="integration-sync-clients-modal-description">
					<FormattedMessage id="integrations.xero.sync_clients.description" />
				</div>
				{this.state.fetchingContacts ? (
					<InlineLoader />
				) : (
					<CustomScrollDiv autoHeight autoHeightMin={10} autoHeightMax={'60vh'}>
						<div className="integration-clients">
							{this.state.xeroContacts && this.state.xeroContacts.length > 0 ? (
								<>
									<div className="integration-client" key={'0'}>
										<Checkbox checked={this.state.selectAll} onClick={this.toggleSelectAll.bind(this)} />
										<FormattedMessage id="common.select_all" />
									</div>
									{this.state.xeroContacts.map(xeroContact => (
										<div className="integration-client" key={xeroContact.id}>
											<Checkbox
												checked={this.state.selectedContacts.includes(xeroContact.id)}
												onClick={this.toggleContact.bind(this, xeroContact.id)}
											/>
											{`${xeroContact.name} (${xeroContact.org})`}
										</div>
									))}
								</>
							) : (
								<FormattedMessage id="integrations.xero.all_contacts_synched" />
							)}
						</div>
					</CustomScrollDiv>
				)}
			</div>
		);

		const selectButtons = [
			{
				text: formatMessage({id: 'common.cancel'}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.WHITE,
			},
			{
				text: formatMessage({id: 'common.create_x_clients'}, {numOfClients: this.state.selectedContacts.length}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.GREEN,
				// T4-135: the preview decides whether there is anything to review. With no groups,
				// or a preview that failed, it imports straight away exactly as it always did.
				callback: this.reviewMatches.bind(this),
				disabled: this.state.importing || this.state.previewing || this.state.selectedContacts.length === 0,
				defaultCallback: () => null,
				preventDefaultClose: true,
			},
		];

		const reviewButtons = [
			{
				text: formatMessage({id: 'integrations.xero.match_review.back'}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.WHITE,
				callback: this.backToSelection.bind(this),
				defaultCallback: () => null,
				preventDefaultClose: true,
			},
			{
				text: formatMessage({id: 'integrations.xero.match_review.import_contacts'}, {count: willImport}),
				style: BUTTON_STYLE.FILLED,
				color: BUTTON_COLOR.GREEN,
				// An arrow, not a bind: GenericModal invokes callbacks with an event, which would
				// arrive as the resolutions argument.
				callback: () =>
					this.importSelected(toResolutions(this.state.matchGroups, this.state.selection), this.state.matchGroups),
				disabled: this.state.importing || willImport === 0,
				defaultCallback: () => null,
				preventDefaultClose: true,
			},
		];

		return (
			<GenericModal
				headerText={
					<FormattedMessage
						id={reviewing ? 'integrations.xero.match_review.title' : 'integrations.xero.sync_clients'}
					/>
				}
				closeModal={this.props.closeModal}
				className="integration-sync-clients-modal"
				buttons={reviewing ? reviewButtons : selectButtons}
				content={
					reviewing ? (
						<XeroMatchReviewStep
							groups={this.state.matchGroups}
							autoLinkCount={this.state.autoLinkCount}
							selectedCount={this.state.selectedContacts.length}
							selection={this.state.selection}
							onSelect={this.selectResolution.bind(this)}
						/>
					) : (
						selectContent
					)
				}
			/>
		);
	}
}

export default injectIntl(withRouter(XeroSyncClientsModal));
